import React, { useState, useMemo, useCallback } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './table';
import { Checkbox } from './checkbox';
import { Input } from './input';
import { Button } from './button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';
import { Trash2, Filter, X, ArrowUpDown, ArrowUp, ArrowDown, Download, FileSpreadsheet, FileText } from 'lucide-react';
import { useTranslation } from '../../contexts/I18nContext';

/**
 * DataTable - Componente tabella riutilizzabile con tutte le funzionalità standard
 * 
 * UTILIZZO:
 * ```jsx
 * <DataTable
 *   columns={[
 *     { key: 'codice', label: 'Codice', width: '100px' },
 *     { key: 'nome', label: 'Nome' },
 *     { key: 'importo', label: 'Importo', align: 'right', format: 'currency' },
 *     { key: 'data', label: 'Data', format: 'date' },
 *     { 
 *       key: 'actions', 
 *       label: 'Azioni', 
 *       filterable: false, 
 *       sortable: false,
 *       exportable: false,
 *       render: (_, row) => <Button onClick={() => edit(row)}>Modifica</Button>
 *     }
 *   ]}
 *   data={items}
 *   onDelete={handleBulkDelete}
 *   idField="id"
 *   exportFileName="esportazione"
 * />
 * ```
 * 
 * Props:
 * @param {Array} columns - Configurazione colonne
 *   - key: string (chiave nel dato)
 *   - label: string (intestazione)
 *   - width?: string (es. '100px', '20%')
 *   - align?: 'left' | 'center' | 'right'
 *   - sortable?: boolean (default: true, false per colonne azione)
 *   - filterable?: boolean (default: true, false per colonne azione)
 *   - exportable?: boolean (default: true, false per escludere dall'export)
 *   - format?: 'text' | 'number' | 'currency' | 'date' | 'percentage'
 *   - render?: (value, row) => ReactNode (rendering personalizzato)
 *   - getValue?: (row) => any (per valori calcolati/nested)
 *   - exportValue?: (row) => any (per valore export personalizzato)
 * @param {Array} data - Array di oggetti da visualizzare
 * @param {Function} onDelete - Callback per eliminazione multipla: (ids) => void
 * @param {boolean} selectable - Abilita selezione multipla (default: true)
 * @param {string} idField - Nome del campo ID (default: 'id')
 * @param {string} emptyMessage - Messaggio quando non ci sono dati
 * @param {boolean} showFiltersDefault - Mostra filtri di default (default: false)
 * @param {string} className - Classi CSS aggiuntive
 * @param {ReactNode} toolbar - Contenuto aggiuntivo nella toolbar (es. pulsante "Nuovo")
 * @param {string} exportFileName - Nome del file per l'export (default: 'esportazione')
 * @param {boolean} showExport - Mostra pulsante export (default: true)
 */
export function DataTable({
  columns,
  data = [],
  onDelete,
  selectable = true,
  idField = 'id',
  emptyMessage,
  showFiltersDefault = false,
  className = "",
  toolbar,
  exportFileName = "esportazione",
  showExport = true,
}) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(showFiltersDefault);
  const [sort, setSort] = useState({ field: null, direction: 'asc' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { t } = useTranslation();

  // Ottiene il valore di una cella
  const getCellValue = useCallback((row, col) => {
    if (col.getValue) return col.getValue(row);
    return row[col.key];
  }, []);

  // Ottiene il valore per l'export
  const getExportValue = useCallback((row, col) => {
    if (col.exportValue) return col.exportValue(row);
    return getCellValue(row, col);
  }, [getCellValue]);

  // Formatta il valore per la visualizzazione
  const formatValue = useCallback((value, format) => {
    if (value === null || value === undefined) return '-';
    
    switch (format) {
      case 'currency':
        return `€ ${parseFloat(value).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      case 'number':
        return parseFloat(value).toLocaleString('it-IT');
      case 'percentage':
        return `${parseFloat(value).toFixed(1)}%`;
      case 'date':
        // Supporta sia YYYY-MM-DD che DD-MM-YYYY
        if (typeof value === 'string' && value.includes('-')) {
          const parts = value.split('-');
          if (parts[0].length === 4) {
            // YYYY-MM-DD -> DD/MM/YYYY
            return `${parts[2]}/${parts[1]}/${parts[0]}`;
          }
        }
        return value;
      default:
        return value;
    }
  }, []);

  // Formatta il valore per l'export (senza simboli valuta per facilitare elaborazione)
  const formatExportValue = useCallback((value, format) => {
    if (value === null || value === undefined) return '';
    
    switch (format) {
      case 'currency':
        return parseFloat(value).toFixed(2);
      case 'number':
        return parseFloat(value);
      case 'percentage':
        return parseFloat(value).toFixed(1);
      case 'date':
        return value;
      default:
        return String(value);
    }
  }, []);

  // Filtra e ordina i dati
  const processedData = useMemo(() => {
    // Prima filtra
    let result = data.filter(row => {
      return Object.entries(filters).every(([key, filterValue]) => {
        if (!filterValue) return true;
        const col = columns.find(c => c.key === key);
        const cellValue = String(getCellValue(row, col) || '').toLowerCase();
        return cellValue.includes(filterValue.toLowerCase());
      });
    });

    // Poi ordina
    if (sort.field) {
      const col = columns.find(c => c.key === sort.field);
      result = [...result].sort((a, b) => {
        let aVal = getCellValue(a, col);
        let bVal = getCellValue(b, col);
        
        // Gestione null/undefined
        if (aVal === null || aVal === undefined) aVal = '';
        if (bVal === null || bVal === undefined) bVal = '';
        
        // Confronto numerico se possibile
        const aNum = parseFloat(aVal);
        const bNum = parseFloat(bVal);
        
        let comparison = 0;
        if (!isNaN(aNum) && !isNaN(bNum)) {
          comparison = aNum - bNum;
        } else {
          comparison = String(aVal).localeCompare(String(bVal), 'it', { sensitivity: 'base' });
        }
        
        return sort.direction === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  }, [data, filters, sort, columns, getCellValue]);

  // Gestione selezione
  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === processedData.length && processedData.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(processedData.map(row => row[idField])));
    }
  }, [processedData, selectedIds.size, idField]);

  const toggleSelect = useCallback((id) => {
    setSelectedIds(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(id)) {
        newSelected.delete(id);
      } else {
        newSelected.add(id);
      }
      return newSelected;
    });
  }, []);

  // Gestione ordinamento
  const handleSort = useCallback((field) => {
    setSort(prev => {
      if (prev.field === field) {
        if (prev.direction === 'asc') return { field, direction: 'desc' };
        if (prev.direction === 'desc') return { field: null, direction: 'asc' };
      }
      return { field, direction: 'asc' };
    });
  }, []);

  // Gestione eliminazione
  const handleBulkDelete = useCallback(() => {
    if (selectedIds.size === 0) return;
    setDeleteDialogOpen(true);
  }, [selectedIds]);

  const confirmBulkDelete = useCallback(() => {
    onDelete?.(Array.from(selectedIds));
    setSelectedIds(new Set());
    setDeleteDialogOpen(false);
  }, [selectedIds, onDelete]);

  // Gestione filtri
  const updateFilter = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
  }, []);

  // ============= EXPORT FUNCTIONS =============
  
  // Colonne esportabili
  const exportableColumns = useMemo(() => 
    columns.filter(col => col.exportable !== false), 
    [columns]
  );

  // Prepara i dati per l'export
  const prepareExportData = useCallback((dataToExport) => {
    return dataToExport.map(row => {
      const exportRow = {};
      exportableColumns.forEach(col => {
        const value = getExportValue(row, col);
        exportRow[col.label] = formatExportValue(value, col.format);
      });
      return exportRow;
    });
  }, [exportableColumns, getExportValue, formatExportValue]);

  // Export CSV
  const exportToCSV = useCallback((dataToExport, suffix = '') => {
    const exportData = prepareExportData(dataToExport);
    if (exportData.length === 0) {
      alert('Nessun dato da esportare');
      return;
    }

    const headers = exportableColumns.map(col => col.label);
    const csvContent = [
      headers.join(';'),
      ...exportData.map(row => 
        headers.map(header => {
          const value = row[header];
          // Escape values with semicolons or newlines
          if (typeof value === 'string' && (value.includes(';') || value.includes('\n') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(';')
      )
    ].join('\n');

    // BOM per Excel UTF-8
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${exportFileName}${suffix}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  }, [prepareExportData, exportableColumns, exportFileName]);

  // Helper per escape XML
  const escapeXml = useCallback((str) => {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }, []);

  // Export Excel (XLSX via simple XML)
  const exportToExcel = useCallback((dataToExport, suffix = '') => {
    const exportData = prepareExportData(dataToExport);
    if (exportData.length === 0) {
      alert('Nessun dato da esportare');
      return;
    }

    const headers = exportableColumns.map(col => col.label);
    
    // Create XML spreadsheet
    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
    xml += '<?mso-application progid="Excel.Sheet"?>\n';
    xml += '<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">\n';
    xml += '<Worksheet ss:Name="Dati">\n<Table>\n';
    
    // Header row
    xml += '<Row>\n';
    headers.forEach(header => {
      xml += `<Cell><Data ss:Type="String">${escapeXml(header)}</Data></Cell>\n`;
    });
    xml += '</Row>\n';
    
    // Data rows
    exportData.forEach(row => {
      xml += '<Row>\n';
      headers.forEach(header => {
        const value = row[header];
        const isNumeric = !isNaN(parseFloat(value)) && isFinite(value);
        const type = isNumeric ? 'Number' : 'String';
        xml += `<Cell><Data ss:Type="${type}">${escapeXml(String(value))}</Data></Cell>\n`;
      });
      xml += '</Row>\n';
    });
    
    xml += '</Table>\n</Worksheet>\n</Workbook>';

    const blob = new Blob([xml], { type: 'application/vnd.ms-excel' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${exportFileName}${suffix}_${new Date().toISOString().split('T')[0]}.xls`;
    link.click();
    URL.revokeObjectURL(link.href);
  }, [prepareExportData, exportableColumns, exportFileName, escapeXml]);

  // Dati selezionati
  const selectedData = useMemo(() => 
    data.filter(row => selectedIds.has(row[idField])),
    [data, selectedIds, idField]
  );

  const hasActiveFilters = Object.values(filters).some(v => v);
  const isAllSelected = processedData.length > 0 && selectedIds.size === processedData.length;
  const isSomeSelected = selectedIds.size > 0 && selectedIds.size < processedData.length;

  // Render icona ordinamento
  const SortIcon = ({ field }) => {
    if (sort.field !== field) return <ArrowUpDown className="h-3 w-3 opacity-30" />;
    return sort.direction === 'asc' 
      ? <ArrowUp className="h-3 w-3" /> 
      : <ArrowDown className="h-3 w-3" />;
  };

  return (
    <>
    <div className={`space-y-3 ${className}`}>
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Button
            variant={showFilters ? "default" : "outline"}
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="gap-1.5"
          >
            <Filter className="h-4 w-4" />
            {t("common.filter")}
            {hasActiveFilters && (
              <span className="ml-1 bg-white/20 text-white rounded-full px-1.5 py-0.5 text-xs font-semibold">
                {Object.values(filters).filter(Boolean).length}
              </span>
            )}
          </Button>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4 mr-1" />
              {t("common.reset")}
            </Button>
          )}
          
          {/* Export Dropdown */}
          {showExport && data.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5">
                  <Download className="h-4 w-4" />
                  {t("common.export")}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {/* Export All */}
                <DropdownMenuItem onClick={() => exportToCSV(data, '_tutti')} className="gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  {t("common.export")} ({data.length}) - CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportToExcel(data, '_tutti')} className="gap-2">
                  <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
                  {t("common.export")} ({data.length}) - Excel
                </DropdownMenuItem>
                
                {/* Export Filtered */}
                {hasActiveFilters && processedData.length !== data.length && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => exportToCSV(processedData, '_filtrati')} className="gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      {t("common.export")} ({processedData.length}) - CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => exportToExcel(processedData, '_filtrati')} className="gap-2">
                      <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
                      {t("common.export")} ({processedData.length}) - Excel
                    </DropdownMenuItem>
                  </>
                )}
                
                {/* Export Selected */}
                {selectedIds.size > 0 && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => exportToCSV(selectedData, '_selezionati')} className="gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      {t("common.export")} ({selectedIds.size}) - CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => exportToExcel(selectedData, '_selezionati')} className="gap-2">
                      <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
                      {t("common.export")} ({selectedIds.size}) - Excel
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {selectable && selectedIds.size > 0 && onDelete && (
            <Button variant="destructive" size="sm" onClick={handleBulkDelete} className="gap-1.5">
              <Trash2 className="h-4 w-4" />
              {t("common.delete")} ({selectedIds.size})
            </Button>
          )}
          {toolbar}
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-xl overflow-hidden shadow-sm bg-card">
        <div className="overflow-x-auto w-full">
          <Table className="w-full table-auto">
            <TableHeader>
              {/* Filter row */}
              {showFilters && (
                <TableRow className="bg-indigo-50/50 dark:bg-indigo-950/20 hover:bg-indigo-50/50 dark:hover:bg-indigo-950/20 border-b-0">
                  {selectable && <TableHead className="w-12" />}
                  {columns.map(col => (
                    <TableHead 
                      key={`filter-${col.key}`} 
                      className="py-2 px-3"
                      style={{ width: col.width }}
                    >
                      {col.filterable !== false ? (
                        <Input
                          placeholder={`${t('common.search')}...`}
                          value={filters[col.key] || ''}
                          onChange={(e) => updateFilter(col.key, e.target.value)}
                          className="h-8 text-xs bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700"
                        />
                      ) : null}
                    </TableHead>
                  ))}
                </TableRow>
              )}
              
              {/* Header row */}
              <TableRow className="bg-slate-50 dark:bg-slate-900/50 hover:bg-slate-50 dark:hover:bg-slate-900/50 border-b-2 border-slate-200 dark:border-slate-700">
                {selectable && (
                  <TableHead className="w-12 px-3">
                    <Checkbox
                      checked={isAllSelected}
                      ref={(el) => { if (el) el.indeterminate = isSomeSelected; }}
                      onCheckedChange={toggleSelectAll}
                      aria-label="Seleziona tutto"
                    />
                  </TableHead>
                )}
                {columns.map(col => (
                  <TableHead 
                    key={col.key}
                    style={{ width: col.width }}
                    className={`
                      px-3 py-3
                      ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'}
                      ${col.sortable !== false ? 'cursor-pointer select-none hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors' : ''}
                    `}
                    onClick={() => col.sortable !== false && handleSort(col.key)}
                  >
                    <div className={`flex items-center gap-1.5 ${col.align === 'right' ? 'justify-end' : col.align === 'center' ? 'justify-center' : ''}`}>
                      <span className="font-semibold text-xs uppercase tracking-wider text-slate-600 dark:text-slate-400">{col.label}</span>
                      {col.sortable !== false && <SortIcon field={col.key} />}
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            
            <TableBody>
              {processedData.length === 0 ? (
                <TableRow>
                  <TableCell 
                    colSpan={columns.length + (selectable ? 1 : 0)} 
                    className="text-center py-16 text-muted-foreground"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <div className="h-12 w-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                        <Filter className="h-5 w-5 text-slate-400" />
                      </div>
                      <p className="font-medium">{hasActiveFilters ? t('common.noResults') : (emptyMessage || t('common.noData'))}</p>
                      {hasActiveFilters && (
                        <Button variant="link" size="sm" onClick={clearFilters}>{t("common.reset")}</Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                processedData.map((row, index) => (
                  <TableRow 
                    key={row[idField] || index}
                    className={`
                      ${selectedIds.has(row[idField]) ? 'bg-indigo-50 dark:bg-indigo-950/30' : ''}
                      hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors
                      border-b border-slate-100 dark:border-slate-800
                    `}
                    data-testid={`row-${row[idField]}`}
                  >
                    {selectable && (
                      <TableCell className="w-12 px-3">
                        <Checkbox
                          checked={selectedIds.has(row[idField])}
                          onCheckedChange={() => toggleSelect(row[idField])}
                        />
                      </TableCell>
                    )}
                    {columns.map(col => {
                      const value = getCellValue(row, col);
                      const displayValue = col.render 
                        ? col.render(value, row) 
                        : formatValue(value, col.format);
                      
                      // Determina se il testo potrebbe essere troncato
                      const rawText = typeof value === 'string' ? value : (value?.toString() || '');
                      const shouldTruncate = !col.render && rawText.length > 30 && col.format !== 'date' && col.format !== 'currency';
                      
                      // Build className with nowrap for dates
                      const cellClasses = [
                        'px-3 py-3',
                        col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : '',
                        col.format === 'currency' ? 'font-mono tabular-nums' : '',
                        col.format === 'date' ? 'whitespace-nowrap' : '',
                        shouldTruncate ? 'max-w-[200px]' : '',
                        col.className || ''
                      ].filter(Boolean).join(' ');
                      
                      // Wrappa in tooltip se testo lungo
                      const cellContent = shouldTruncate ? (
                        <TooltipProvider delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="block truncate cursor-help">{displayValue}</span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-sm break-words">
                              <p>{rawText}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : displayValue;
                      
                      return (
                        <TableCell 
                          key={`${row[idField]}-${col.key}`}
                          className={cellClasses}
                          style={{ width: col.width, minWidth: col.minWidth }}
                        >
                          {cellContent}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      
      {/* Footer info */}
      <div className="flex justify-between items-center text-sm text-muted-foreground px-1">
        <span>
          <span className="font-medium text-foreground">{processedData.length}</span> {t("common.rows")}
          {hasActiveFilters && <span className="text-indigo-600"> ({data.length})</span>}
        </span>
        {selectable && selectedIds.size > 0 && (
          <span className="font-medium text-indigo-600">{selectedIds.size}</span>
        )}
      </div>
    </div>

    {/* Dialog conferma eliminazione */}
    <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("confirm.confirmDeletion")}</DialogTitle>
          <DialogDescription>
            {t("confirm.deleteConfirmMessage")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="destructive" onClick={confirmBulkDelete} data-testid="confirm-bulk-delete-btn">{t("common.delete")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

export default DataTable;
